import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import PlanTable from "./components/plan-table";
import { Body } from "../../../components/main-body/body";
import ApiPlanManagement from "../../../api/plan-management";
import ApiCohortManagement from "../../../api/cohort-management";
import PlanPage from "./components/plan-page";
import "./plan-management.css";
import useNotification from "../../../components/notification/notification";
import { useParams, useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import ApiSubjectsDashboard from "../../../api/subjects-dashboard";
import debounce from "../../../common/utils/debounce";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";

const ALLOWED_STATUSES = new Set(["active", "pending", "inactive"]);
const PLAN_DETAIL_REFETCH_ACTIONS = new Set([
  "default_data_updated",
  "schedule_updated",
  "week_added",
  "week_reordered",
  "week_duplicated",
  "week_deleted",
  "activated",
  "discarded",
  "date_activity_upserted",
  "weekly_activity_upserted",
  "weekly_activity_deleted",
  "date_activity_deleted",
]);

function parseStatusesFromSearch(search) {
  const sp = new URLSearchParams(search || "");

  const rawAll = sp.getAll("statuses").flatMap((v) => String(v).split(","));

  const statuses = rawAll
    .map((v) => v.trim())
    .filter(Boolean)
    .filter((v) => ALLOWED_STATUSES.has(v));

  return statuses.length ? statuses : null;
}

const PlanManagement = () => {
  const { t } = useTranslation();
  // LF360-2024: creating needs `content.edit` on the server.
  const canCreate = useCan("content.edit");
  const { openNotification } = useNotification();

  const [plans, setPlans] = useState([]);
  const [selectedPlan, setPlan] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [changeLogs, setChangeLogs] = useState({});

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [isDetailLoading, setDetailLoading] = useState(false);
  const reqIdRef = useRef(0);

  const [cohortsList, setCohortsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const { id } = useParams();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const statusesFromUrl = parseStatusesFromSearch(location.search);
    return statusesFromUrl ? { statuses: statusesFromUrl } : {};
  });

  const handleNavigate = (path) => {
    if (location.pathname !== path) navigate(path);
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })));
    });
  };

  const mergeUniqueById = (prev, incoming) => {
    const seen = new Set(prev.map((p) => p.id));
    return prev.concat((incoming || []).filter((p) => !seen.has(p.id)));
  };

  const mergeChangeLogs = (prev = {}, incoming = {}) => {
    const out = { ...prev };
    for (const [planId, trails] of Object.entries(incoming)) {
      const existed = out[planId] || [];
      const existedIds = new Set(existed.map((t) => t.id));
      out[planId] = existed.concat((trails || []).filter((t) => !existedIds.has(t.id)));
    }
    return out;
  };

  const syncPlanInList = (plan) => {
    if (!plan?.id) return;

    setPlans((prev) =>
      (prev || []).map((item) =>
        String(item.id) === String(plan.id)
          ? {
              ...item,
              title: plan.title,
              status: plan.status,
              updatedAt: plan.updatedAt,
              createdAt: plan.createdAt,
              cohorts: plan.cohorts || item.cohorts,
            }
          : item
      )
    );
  };

  const syncScenarioInPlan = useCallback((scenario) => {
    if (!scenario?.id) return;

    setPlan((prev) => {
      if (!prev?.WeeklySchedules?.length) return prev;

      return {
        ...prev,
        WeeklySchedules: prev.WeeklySchedules.map((weeklySchedule) => ({
          ...weeklySchedule,
          scenarios: (weeklySchedule.scenarios || []).map((activity) => {
            const nestedScenarioId = activity?.Scenario?.id || activity?.scenarioId;

            if (String(nestedScenarioId) !== String(scenario.id)) return activity;

            return {
              ...activity,
              Scenario: {
                ...(activity.Scenario || {}),
                ...scenario,
              },
            };
          }),
          activities: (weeklySchedule.activities || []).map((activity) => {
            const nestedScenarioId = activity?.Scenario?.id || activity?.scenarioId;

            if (String(nestedScenarioId) !== String(scenario.id)) return activity;

            return {
              ...activity,
              Scenario: {
                ...(activity.Scenario || {}),
                ...scenario,
              },
            };
          }),
        })),
      };
    });
  }, []);

  const removeScenarioFromPlan = useCallback((deletedIds) => {
    if (!deletedIds.length) return;
    const deletedIdSet = new Set(deletedIds.map(String));

    setPlan((prev) => {
      if (!prev?.WeeklySchedules?.length) return prev;

      return {
        ...prev,
        WeeklySchedules: prev.WeeklySchedules.map((weeklySchedule) => ({
          ...weeklySchedule,
          scenarios: (weeklySchedule.scenarios || []).filter(
            (activity) => !deletedIdSet.has(String(activity?.Scenario?.id || activity?.scenarioId))
          ),
          activities: (weeklySchedule.activities || []).filter(
            (activity) => !deletedIdSet.has(String(activity?.Scenario?.id || activity?.scenarioId))
          ),
        })),
      };
    });
  }, []);

  const loadPage = async (nextPage, { replace = false, query: queryOverride } = {}) => {
    if (isLoading && !replace) return;

    const myReqId = ++reqIdRef.current;

    setLoading(true);
    try {
      const { data } = await ApiPlanManagement.getPlans({
        simple: true,
        query: (queryOverride ?? searchValue) || "",
        ...filters,
        page: nextPage,
      });

      if (myReqId !== reqIdRef.current) return;

      const incomingPlans = data?.plans || [];
      setHasMore(Boolean(data?.hasMore));
      setPage(Number.isFinite(data?.page) ? data.page : nextPage);

      if (nextPage === 1) {
        const groupSchedules = data?.groupSchedules || [];
        setCohorts(
          groupSchedules.map((cohort) => ({
            label: cohort.EnrollmentFieldOption?.value,
            id: cohort.id,
          }))
        );
        setScenarios(data?.scenarios || []);
      }

      setChangeLogs((prev) =>
        replace ? (data?.changeLogs || {}) : mergeChangeLogs(prev, data?.changeLogs || {})
      );

      setPlans((prev) => (replace ? incomingPlans : mergeUniqueById(prev, incomingPlans)));
    } catch (e) {
      console.log("Plans fetch error", e);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  const fetchNext = () => {
    if (!hasMore || isLoading) return;
    return loadPage(page + 1, { query: searchValue });
  };

  const resetAndFetch = async (q) => {
    setPlans([]);
    setPage(0);
    setHasMore(true);
    await loadPage(1, { replace: true, query: q });
  };

  const refreshCohorts = async () => {
    try {
      const [{ data }] = await Promise.all([
        ApiCohortManagement.getCohorts(),
        loadPage(1, { replace: true, query: searchValue }),
      ]);

      setCohortsList((data?.cohorts || []).map((c) => ({ id: c.id, name: c.value })));
    } catch (error) {
      console.error("Failed to refresh plan cohorts after admin event", error);
    }
  };

  const refreshScenarios = useCallback(
    async (payload) => {
      const deletedIds = [
        ...(payload?.deletedIds || []),
        payload?.id,
      ].filter(Boolean);

      if (deletedIds.length && (payload?.action === "deleted" || payload?.action === "bulk_deleted")) {
        const deletedIdSet = new Set(deletedIds.map(String));
        setScenarios((prev) => (prev || []).filter((scenario) => !deletedIdSet.has(String(scenario.id))));
        removeScenarioFromPlan(deletedIds);
      } else if (payload?.scenario?.id) {
        setScenarios((prev) =>
          (prev || []).map((scenario) =>
            String(scenario.id) === String(payload.scenario.id)
              ? { ...scenario, ...payload.scenario }
              : scenario
          )
        );
        syncScenarioInPlan(payload.scenario);
      }

      await loadPage(1, { replace: true, query: searchValue });
    },
    [loadPage, removeScenarioFromPlan, searchValue, syncScenarioInPlan]
  );

  const debouncedSearch = useMemo(() => debounce((q) => resetAndFetch(q), 400), [filters]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const headerProps = useMemo(
    () => ({
      title: "Plan management",
      create: canCreate && {
        label: t("plan_management.create"),
        onClick: () => createNewPlan(),
      },
      search: {
        value: searchValue,
        onHandleChange: handleUpdateSearchValue,
      },
    }),
    [searchValue, t, canCreate]
  );

  const createNewPlan = () => {
    ApiPlanManagement.createPlan()
      .then((r) => {
        resetAndFetch();
        openNotification({
          type: "success",
          message: (
            <p>
              {t("plan_management.notification.created", {
                plan: r.data.plan.title,
              })}
            </p>
          ),
        });
      })
      .catch(() => {
        openNotification({
          type: "error",
          message: <p>{t("plan_management.notification.error")}</p>,
        });
      });
  };

  const handleSelectPlan = (plan) => {
    handleNavigate(`/content/plans/${plan.id}`);
  };

  const handleDeselectPlan = () => {
    handleNavigate(`/content/plans`);
  };

  const removePlan = (planId, planTitle) => {
    ApiPlanManagement.deletePlan(planId).then(() => {
      resetAndFetch();
      openNotification({
        type: "success",
        message: (
          <p>
            {t("plan_management.notification.deleted", {
              plan: planTitle,
            })}
          </p>
        ),
      });
    });
  };

  const duplicatePlan = (id) => {
    ApiPlanManagement.duplicatePlan(id).finally(() => {
      resetAndFetch();
    });
  };

  const loadSinglePlan = async (planId) => {
    if (!planId) return;
    setDetailLoading(true);
    try {
      const { data } = await ApiPlanManagement.getPlanById(planId);
      const fullPlan = data?.plan;
      const logs = data?.changeLogs || {};
      setPlan(fullPlan);
      syncPlanInList(fullPlan);
      setChangeLogs((prevLogs) => ({ ...prevLogs, ...logs }));
    } catch (e) {
      console.error("Error fetching full plan", e);
      openNotification({ type: "error", message: "Failed to load plan details." });
      handleNavigate("/content/plans");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshPlans = async (payload) => {
    const deletedIds = [
      ...(payload?.deletedIds || []),
      payload?.id,
    ].filter(Boolean).map(String);

    const isDeleted = payload?.action === "deleted" || payload?.action === "bulk_deleted";
    const changedPlanId = payload?.plan?.id || payload?.id;
    const isCurrentPlanEvent = id && changedPlanId && String(id) === String(changedPlanId);

    if (isDeleted && deletedIds.length) {
      setPlans((prev) => (prev || []).filter((plan) => !deletedIds.includes(String(plan.id))));

      if (id && deletedIds.includes(String(id))) {
        setPlan(null);
        handleNavigate("/content/plans");
        await resetAndFetch(searchValue);
        return;
      }
    }

    if (payload?.plan?.id) {
      syncPlanInList(payload.plan);
      setPlan((prev) =>
        prev?.id && String(prev.id) === String(payload.plan.id)
          ? { ...prev, ...payload.plan }
          : prev
      );
    }

    if (isCurrentPlanEvent && PLAN_DETAIL_REFETCH_ACTIONS.has(payload?.action)) {
      await loadSinglePlan(id);
      return;
    }

    if (!isCurrentPlanEvent) {
      await resetAndFetch(searchValue);
    }
  };

  useAdminEvents({
    onCohortChanged: refreshCohorts,
    onScenarioChanged: refreshScenarios,
    onPlanChanged: refreshPlans,
  });

  useEffect(() => {
    getDashboardFilterItems();
    resetAndFetch(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skipFirstFiltersRunRef = useRef(true);
  useEffect(() => {
    if (skipFirstFiltersRunRef.current) {
      skipFirstFiltersRunRef.current = false;
      return;
    }
    resetAndFetch(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (id) {
      loadSinglePlan(id);
    } else {
      setPlan(null);
    }
  }, [id]);

  const updateFilterValue = (value) => setFilters(value);

  const statuses = [
    { id: "active", name: "Active" },
    { id: "pending", name: "Pending" },
    { id: "inactive", name: "Inactive" },
  ];

  const handleBulkDeleteTasks = async (ids, { onDone } = {}) => {
    try {
      const resp = await ApiPlanManagement.deletePlans(ids);
      const { deletedIds = [], failed = [] } = resp?.data || {};

      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "items were" : "item was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        const plural = failedIds.length > 1;
        openNotification?.({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length} {plural ? "items" : "item"}
            </p>
          ),
        });
      }

      onDone?.({ failedIds });

      await resetAndFetch(searchValue);
    } catch (e) {
      console.error(e);

      openNotification?.({
        type: "error",
        message: <p>Failed to delete tasks</p>,
      });

      onDone?.({ failedIds: ids });
    }
  };

  if (id) {
    return (
      <Body>
        {!selectedPlan ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          </div>
        ) : (
          <>
            {isDetailLoading && (
              <div style={{ position: "absolute", right: 24, top: 24, zIndex: 10 }}>
                <Spin />
              </div>
            )}
            <PlanPage
              plan={selectedPlan}
              scenarios={scenarios}
              cohorts={cohorts}
              toList={handleDeselectPlan}
              refreshPlan={() => loadSinglePlan(id)}
              openNotification={openNotification}
              changeLogs={changeLogs}
            />
          </>
        )}
      </Body>
    );
  }

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          mode="controlled"
          filter={filters}
          filtersBy={[
            { name: "Cohort", id: "cohortIds", children: cohortsList },
            { name: "Status", id: "statuses", children: statuses },
          ]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      <PlanTable
        data={plans}
        onHandleRowClick={handleSelectPlan}
        removePlan={removePlan}
        duplicatePlan={duplicatePlan}
        isLoading={isLoading}
        hasMore={hasMore}
        fetchData={fetchNext}
        onBulkDelete={handleBulkDeleteTasks}
      />
    </Body>
  );
};

export default PlanManagement;
